import type { ClientContext } from "./context"
import type { Message, PaginatedResponse, PaginationQuery } from "./contract/query"
import type { MessageRelations } from "./contract/relations"

/**
 * Frontend comment creation request (content is the positional argument of
 * {@link CommentsClient.create}). `threadRootId` and `replyToId` are the
 * independent semantic relation fields (backend `thread_root` / `reply_to`);
 * neither implies the other.
 */
export interface CreateCommentRequest extends Partial<MessageRelations> {
  displayName?: string
  media?: { url: string; kind?: string } | null
  idempotencyKey?: string
  signal?: AbortSignal
}

function newIdempotencyKey(): string {
  const c = globalThis.crypto as unknown as Crypto & { randomUUID?: () => string }
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID()
  }
  const b = new Uint8Array(16)
  globalThis.crypto.getRandomValues(b)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export class CommentsClient {
  constructor(private readonly ctx: ClientContext) {}

  private async signWithPipeline(parts: (string | null | undefined)[], signal?: AbortSignal) {
    return this.ctx.signingPipeline.sign(parts, signal)
  }

  private async queryComments(
    body: PaginationQuery,
    signal?: AbortSignal,
  ): Promise<PaginatedResponse> {
    const personalization = await this.ctx.signingPipeline.signQuery(
      this.ctx.siteId,
      this.ctx.pageSlug,
    )
    if (personalization) {
      body.author_public_key = personalization.author_public_key
      body.author_signature = personalization.author_signature
    }
    const res = await this.ctx.transport.request<PaginatedResponse>(
      "QUERY",
      `/api/v1/sites/${encodeURIComponent(this.ctx.siteId)}/pages/${encodeURIComponent(this.ctx.pageSlug)}/comments`,
      {
        body,
        signal,
      },
    )
    return res.data
  }

  async list(pagination: PaginationQuery = {}, signal?: AbortSignal): Promise<PaginatedResponse> {
    return this.queryComments({ ...pagination }, signal)
  }

  /**
   * Backend-authoritative Thread member page (contract: `queryComments` with
   * `thread_root`). Only active messages whose `thread_root` equals rootId are
   * returned in backend canonical ordering; the root itself is excluded and
   * `meta.total` is the active reply count.
   */
  async listThread(
    rootId: string,
    pagination: PaginationQuery = {},
    signal?: AbortSignal,
  ): Promise<PaginatedResponse> {
    return this.queryComments({ ...pagination, thread_root: rootId }, signal)
  }

  /**
   * Fetch a single comment (contract: `getComment`) — e.g. a Thread root that
   * is not already present in local state. 404 for missing/out-of-scope ids.
   */
  async get(commentId: string, signal?: AbortSignal): Promise<Message> {
    const res = await this.ctx.transport.request<Message>(
      "GET",
      `/api/v1/sites/${encodeURIComponent(this.ctx.siteId)}/pages/${encodeURIComponent(this.ctx.pageSlug)}/comments/${encodeURIComponent(commentId)}`,
      { signal },
    )
    return res.data
  }

  async create(
    content: string,
    options: CreateCommentRequest = {},
  ): Promise<{ submission_id: number }> {
    const signedContent = options.media?.url ?? content
    const signed = await this.signWithPipeline(
      [
        "POST",
        this.ctx.siteId,
        this.ctx.pageSlug,
        signedContent,
        options.replyToId ?? null,
        options.threadRootId ?? null,
      ],
      options.signal,
    )
    const body = {
      content,
      media: options.media ?? null,
      display_name: options.displayName ?? "Anonymous",
      author_public_key: signed.author_public_key,
      author_signature: signed.author_signature,
      reply_to: options.replyToId ?? null,
      thread_root: options.threadRootId ?? null,
      challenge_response: signed.challenge_response,
    }
    const res = await this.ctx.transport.request<{ submission_id: number }>(
      "POST",
      `/api/v1/sites/${encodeURIComponent(this.ctx.siteId)}/pages/${encodeURIComponent(this.ctx.pageSlug)}/comments`,
      {
        body,
        headers: { "Idempotency-Key": options.idempotencyKey ?? newIdempotencyKey() },
        signal: options.signal,
      },
    )
    return res.data
  }

  async update(
    commentId: string,
    content: string,
    options: { idempotencyKey?: string; signal?: AbortSignal } = {},
  ): Promise<{ submission_id: number }> {
    const signed = await this.signWithPipeline(
      ["PATCH", this.ctx.siteId, this.ctx.pageSlug, commentId, content],
      options.signal,
    )
    const body = {
      content,
      author_public_key: signed.author_public_key,
      author_signature: signed.author_signature,
      challenge_response: signed.challenge_response,
    }
    const res = await this.ctx.transport.request<{ submission_id: number }>(
      "PATCH",
      `/api/v1/sites/${encodeURIComponent(this.ctx.siteId)}/pages/${encodeURIComponent(this.ctx.pageSlug)}/comments/${encodeURIComponent(commentId)}`,
      {
        body,
        headers: { "Idempotency-Key": options.idempotencyKey ?? newIdempotencyKey() },
        signal: options.signal,
      },
    )
    return res.data
  }

  async remove(
    commentId: string,
    options: { idempotencyKey?: string; signal?: AbortSignal } = {},
  ): Promise<{ submission_id: number }> {
    const signed = await this.signWithPipeline(
      ["DELETE", this.ctx.siteId, this.ctx.pageSlug, commentId],
      options.signal,
    )
    const body = {
      author_public_key: signed.author_public_key,
      author_signature: signed.author_signature,
      challenge_response: signed.challenge_response,
    }
    const res = await this.ctx.transport.request<{ submission_id: number }>(
      "DELETE",
      `/api/v1/sites/${encodeURIComponent(this.ctx.siteId)}/pages/${encodeURIComponent(this.ctx.pageSlug)}/comments/${encodeURIComponent(commentId)}`,
      {
        body,
        headers: { "Idempotency-Key": options.idempotencyKey ?? newIdempotencyKey() },
        signal: options.signal,
      },
    )
    return res.data
  }
}
